import { useEffect, useState } from "react";
import { getSiteContent } from "@/services/siteContentService";
import DEFAULT_CONTENT from "./defaultContent";

const CACHE_PREFIX = "siteContent:";

function readCache(pageKey) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + pageKey);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(pageKey, blocks) {
  try {
    localStorage.setItem(CACHE_PREFIX + pageKey, JSON.stringify(blocks));
  } catch {
    // Storage full/blocked — just skip caching.
  }
}

/** Reads a page's published CMS blocks for public rendering. Fetch-once
 * (matches every other service in this app — none use react-query's
 * live-subscription features), falling back to the page's default/seed
 * content whenever there's no Firestore doc yet or nothing has been
 * published — so a page never regresses to blank before an admin's first
 * Publish.
 *
 * The last published blocks are cached in localStorage and rendered
 * immediately on the next visit (then refreshed from Firestore), so the
 * hardcoded defaults — e.g. the old hero images — never flash on screen
 * before the real content arrives. On a device's very first visit there's
 * no cache yet: `blocks` is empty and `ready` is false until the fetch
 * resolves, so callers can show a placeholder instead of stale defaults. */
export function useSiteContent(pageKey) {
  const [cached, setCached] = useState(() => readCache(pageKey));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setCached(readCache(pageKey));
      try {
        const data = await getSiteContent(pageKey);
        const published = data?.published?.blocks;
        const next =
          Array.isArray(published) && published.length > 0
            ? published
            : DEFAULT_CONTENT[pageKey] || [];
        if (!cancelled) {
          setCached(next);
          writeCache(pageKey, next);
        }
      } catch (err) {
        console.error(`Failed to load site content for "${pageKey}":`, err);
        // Keep showing cached content, or defaults, rather than an empty page.
        if (!cancelled) {
          setCached((prev) => prev ?? (DEFAULT_CONTENT[pageKey] || []));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  return { blocks: cached ?? [], loading, ready: cached !== null };
}
